/**
 * A report's period and label in words, for the screens, the client portal and Teslyn's report emails (F07). It
 * imports nothing, so the Edge Functions use the same words (tools/dev/copy-edge-shared.mjs builds it with
 * `app-email`). `portal.ts` re-exports both.
 */

/** The kinds a report can be (the `report.kind` check). */
export type ReportLabelKind = 'daily' | 'weekly' | 'final' | 'qc';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "16 Oct 2026" from an ISO date, "Week 41, 2026" from a weekly period (`2026-WK41`; "Week 41" for a `WK41` from
 * before decision 487); any other period is returned as written.
 */
export function periodText(period: string): string {
  const w = /^(?:(\d{4})-)?WK(\d{2})$/.exec(period);
  if (w) return w[1] ? `Week ${Number(w[2])}, ${w[1]}` : `Week ${Number(w[2])}`;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(period);
  if (!m) return period;
  const month = MONTHS[Number(m[2]) - 1];
  return month ? `${Number(m[3])} ${month} ${m[1]}` : period;
}

const KIND_LABEL: Readonly<Record<ReportLabelKind, string>> = {
  daily: 'Daily report',
  weekly: 'Weekly report',
  final: 'Final report',
  qc: 'QC report',
};

/**
 * "Daily report, 16 Oct 2026", "Weekly report, Week 42, 2026": the report's name wherever the client reads it (the
 * portal, its emails). It never carries the revision number (R139, MB 2 Nov 2026): the database still counts
 * revisions (decision 30) and a newer one replaces the older for the client (decision 241), so the client only ever
 * sees one report of each period.
 */
export function reportLabel(r: {
  readonly kind: ReportLabelKind;
  readonly period: string;
  readonly revision: number;
}): string {
  return `${KIND_LABEL[r.kind]}, ${periodText(r.period)}`;
}

/** The report's name for staff, with " (revision 2)" from the second revision on, so they publish the right one. */
export function staffReportLabel(r: {
  readonly kind: ReportLabelKind;
  readonly period: string;
  readonly revision: number;
}): string {
  const rev = r.revision > 1 ? ` (revision ${r.revision})` : '';
  return `${reportLabel(r)}${rev}`;
}
